<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import VariableList from 'dashboard/components/widgets/conversation/VariableList.vue';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

import ConversationsAPI from 'dashboard/api/conversations';

export default {
    name: 'AddScheduledMessage',
    components: {
        NextButton,
        VariableList,
        TemplatesPicker,
        WhatsAppTemplateParser,
    },
    props: {
        onClose: {
            type: Function,
            default: () => { },
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        initialData: {
            type: Object,
            default: () => ({}),
        },
    },
    setup() {
        return { v$: useVuelidate() };
    },
    data() {
        return {
            conversationId: this.initialData.conversation_id || '',
            content: this.initialData.content || '',
            scheduledAt: this.initialData.scheduled_at
                ? this.formatDateTimeLocal(this.initialData.scheduled_at)
                : '',
            isSubmitting: false,
            showVariables: false,
            variableSearchTerm: '',
            // WhatsApp template state
            isWhatsAppCloud: false,
            inboxId: null,
            selectedTemplate: null,
            templateParams: null,
            isCheckingConversation: false,
        };
    },
    validations: {
        conversationId: { required },
        scheduledAt: { required },
    },
    computed: {
        isFormInvalid() {
            if (this.v$.conversationId.$invalid || this.v$.scheduledAt.$invalid) {
                return true;
            }
            // For WhatsApp Cloud, require a template to be selected and configured
            if (this.isWhatsAppCloud) {
                return !this.templateParams;
            }
            // For other channels, require content
            return !this.content;
        },
        modalTitle() {
            return this.editMode
                ? this.$t('SCHEDULED_MSGS.EDIT.TITLE')
                : this.$t('SCHEDULED_MSGS.ADD.TITLE');
        },
    },
    watch: {
        conversationId(newVal) {
            if (newVal) {
                this.checkConversationInbox(newVal);
            } else {
                this.isWhatsAppCloud = false;
                this.inboxId = null;
                this.selectedTemplate = null;
                this.templateParams = null;
            }
        },
    },
    mounted() {
        if (this.conversationId) {
            this.checkConversationInbox(this.conversationId);
        }
    },
    methods: {
        formatDateTimeLocal(dateStr) {
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        },
        resetForm() {
            this.conversationId = '';
            this.content = '';
            this.scheduledAt = '';
            this.showVariables = false;
            this.variableSearchTerm = '';
            this.isWhatsAppCloud = false;
            this.inboxId = null;
            this.selectedTemplate = null;
            this.templateParams = null;
            this.v$.$reset();
        },
        async checkConversationInbox(conversationId) {
            this.isCheckingConversation = true;
            this.isWhatsAppCloud = false;
            this.inboxId = null;
            this.selectedTemplate = null;
            this.templateParams = null;
            try {
                // Try to get the conversation from the store first
                const storeConversation = this.$store.getters.getConversationById(
                    Number(conversationId)
                );
                if (storeConversation && storeConversation.inbox_id) {
                    this.resolveInbox(storeConversation.inbox_id);
                } else {
                    // Fetch from API
                    const response = await ConversationsAPI.show(conversationId);
                    if (response.data && response.data.inbox_id) {
                        this.resolveInbox(response.data.inbox_id);
                    }
                }
            } catch {
                // Conversation not found or error, stay with default behavior
                this.isWhatsAppCloud = false;
            } finally {
                this.isCheckingConversation = false;
            }
        },
        resolveInbox(inboxIdVal) {
            const inbox = this.$store.getters['inboxes/getInbox'](inboxIdVal);
            if (inbox && inbox.channel_type === 'Channel::Whatsapp') {
                this.isWhatsAppCloud = true;
                this.inboxId = inboxIdVal;
                // Clear free-text content since we'll use template
                this.content = '';
            } else {
                this.isWhatsAppCloud = false;
                this.inboxId = null;
            }
        },
        onSelectTemplate(template) {
            this.selectedTemplate = template;
        },
        onResetTemplate() {
            this.selectedTemplate = null;
            this.templateParams = null;
        },
        onTemplateSendMessage(payload) {
            // payload: { message, templateParams: { name, category, language, namespace, processed_params } }
            this.content = payload.message;
            this.templateParams = payload.templateParams;
        },
        insertVariable(variableKey) {
            const tag = `{{${variableKey}}}`;
            const textarea = this.$refs.contentTextarea;
            if (textarea) {
                const cursorPos = textarea.selectionStart;
                const textBefore = this.content.substring(0, cursorPos);
                const triggerIdx = textBefore.lastIndexOf('{{');
                if (triggerIdx !== -1) {
                    this.content =
                        this.content.substring(0, triggerIdx) +
                        tag +
                        this.content.substring(cursorPos);
                    this.$nextTick(() => {
                        const newPos = triggerIdx + tag.length;
                        textarea.focus();
                        textarea.setSelectionRange(newPos, newPos);
                    });
                } else {
                    this.content += tag;
                }
            } else {
                this.content += tag;
            }
            this.showVariables = false;
            this.variableSearchTerm = '';
        },
        handleContentInput(event) {
            const val = event.target.value;
            const cursorPos = event.target.selectionStart;
            const textBefore = val.substring(0, cursorPos);
            const triggerIdx = textBefore.lastIndexOf('{{');

            if (triggerIdx !== -1) {
                const textAfterTrigger = textBefore.substring(triggerIdx + 2);
                if (!textAfterTrigger.includes('}}')) {
                    this.showVariables = true;
                    this.variableSearchTerm = textAfterTrigger;
                    return;
                }
            }
            this.showVariables = false;
            this.variableSearchTerm = '';
        },
        handleContentKeydown(event) {
            if (event.key === 'Escape' && this.showVariables) {
                this.showVariables = false;
                event.stopPropagation();
            }
        },
        async submitForm() {
            this.isSubmitting = true;
            const data = {
                conversation_id: this.conversationId,
                scheduled_at: new Date(this.scheduledAt).toISOString(),
            };

            if (this.isWhatsAppCloud && this.templateParams) {
                data.template_params = this.templateParams;
                data.content = this.content || '';
            } else {
                data.content = this.content;
            }

            try {
                if (this.editMode) {
                    await this.$store.dispatch('updateScheduledMessage', {
                        id: this.initialData.id,
                        ...data,
                    });
                    useAlert(this.$t('SCHEDULED_MSGS.EDIT.API.SUCCESS_MESSAGE'));
                } else {
                    await this.$store.dispatch('createScheduledMessage', data);
                    useAlert(this.$t('SCHEDULED_MSGS.ADD.API.SUCCESS_MESSAGE'));
                }
                this.isSubmitting = false;
                this.resetForm();
                this.onClose();
            } catch (error) {
                this.isSubmitting = false;
                const errorMessage =
                    error?.message ||
                    (this.editMode
                        ? this.$t('SCHEDULED_MSGS.EDIT.API.ERROR_MESSAGE')
                        : this.$t('SCHEDULED_MSGS.ADD.API.ERROR_MESSAGE'));
                useAlert(errorMessage);
            }
        },
    },
};
</script>

<template>
    <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header :header-title="modalTitle" :header-content="$t('SCHEDULED_MSGS.ADD.DESC')" />
        <form class="flex flex-col w-full" @submit.prevent="submitForm">
            <div class="w-full">
                <label :class="{ error: v$.conversationId.$error }">
                    {{ $t('SCHEDULED_MSGS.ADD.FORM.CONVERSATION.LABEL') }}
                    <input v-model="conversationId" type="number" min="1"
                        :placeholder="$t('SCHEDULED_MSGS.ADD.FORM.CONVERSATION.PLACEHOLDER')" :disabled="editMode"
                        @blur="v$.conversationId.$touch" />
                </label>
            </div>

            <div v-if="isCheckingConversation" class="flex items-center gap-2 py-2 text-sm text-n-slate-11">
                <span class="i-lucide-loader-2 size-4 animate-spin" />
                Verificando inbox...
            </div>

            <div v-else-if="isWhatsAppCloud" class="w-full">
                <div class="flex items-center gap-2 px-3 py-2 mb-3 rounded-lg bg-n-amber-2 text-n-amber-11">
                    <span class="i-lucide-alert-triangle size-4 flex-shrink-0" />
                    <p class="text-xs">
                        Esta conversa pertence a um inbox WhatsApp Cloud. Devido à janela de 24h,
                        mensagens programadas devem usar um <strong>template aprovado</strong>.
                    </p>
                </div>

                <div v-if="!selectedTemplate">
                    <label class="block mb-1 text-sm font-medium">
                        Selecione um template
                    </label>
                    <TemplatesPicker :inbox-id="inboxId" @on-select="onSelectTemplate" />
                </div>

                <div v-else>
                    <WhatsAppTemplateParser :template="selectedTemplate" @send-message="onTemplateSendMessage"
                        @reset-template="onResetTemplate">
                        <template #actions="{ sendMessage, resetTemplate, disabled }">
                            <footer class="flex gap-2 justify-end mt-3">
                                <NextButton faded slate type="button" label="Trocar template" @click="resetTemplate" />
                                <NextButton type="button"
                                    :label="templateParams ? '✓ Template configurado' : 'Confirmar template'"
                                    :disabled="disabled" :class="templateParams ? 'ring-2 ring-n-teal-7' : ''"
                                    @click="sendMessage" />
                            </footer>
                        </template>
                    </WhatsAppTemplateParser>
                </div>
            </div>

            <div v-else class="w-full relative">
                <label :class="{ error: !content && v$.conversationId.$dirty }">
                    {{ $t('SCHEDULED_MSGS.ADD.FORM.CONTENT.LABEL') }}
                    <textarea ref="contentTextarea" v-model="content" rows="5"
                        :placeholder="$t('SCHEDULED_MSGS.ADD.FORM.CONTENT.PLACEHOLDER')" @input="handleContentInput"
                        @keydown="handleContentKeydown" />
                </label>
                <div v-if="showVariables" class="absolute z-50 left-0 bottom-2 w-full">
                    <VariableList :search-key="variableSearchTerm" @select-variable="insertVariable" />
                </div>
            </div>

            <div class="w-full">
                <label :class="{ error: v$.scheduledAt.$error }">
                    {{ $t('SCHEDULED_MSGS.ADD.FORM.SCHEDULED_AT.LABEL') }}
                    <input v-model="scheduledAt" type="datetime-local" :min="formatDateTimeLocal(new Date())"
                        @blur="v$.scheduledAt.$touch" />
                </label>
            </div>

            <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
                <NextButton faded slate type="reset" :label="$t('SCHEDULED_MSGS.ADD.CANCEL_BUTTON_TEXT')"
                    @click.prevent="onClose" />
                <NextButton type="submit" :label="$t('SCHEDULED_MSGS.ADD.FORM.SUBMIT')"
                    :disabled="isFormInvalid || isSubmitting" :is-loading="isSubmitting" />
            </div>
        </form>
    </div>
</template>
